/* ============================================================
   BOOST — halaman PARTNER MCN  (/mcn)   · UI English (alat partner/admin)
   ============================================================
   PLAN_BOOST.md §🔄 [2026-11-04]. Staff MCN (cth Gorilla Cat) log masuk dengan
   akaun SESAAT sendiri; server menyemak email mereka dalam `boost_mcn.partnerEmails`.
   Mereka nampak pemohon MCN mereka SAHAJA. Admin nampak semua MCN (?m=<mcnId>).

   Setiap program:
     · Google Sheet — toggle auto save + link sheet + email robot + Test connection
       (Test hanya MEMBACA baris 1; tidak menulis ke sheet MCN).
     · Send to sheet — BULK: hantar semua pemohon yang belum masuk sheet (gagal /
       sebelum toggle hidup), bukan satu-satu.
     · Senarai pemohon + carian + Export CSV + Reject / Approve semula.
   ⛔ Tiada PIN partner (fasa 1) — pintu = login + email dalam senarai MCN (server).
   ============================================================ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, Download, Search, Copy, Check, Send, Users } from 'lucide-react';

import '../design/tokens.css';
import { PageHeader, Card, CardBody, Btn, Badge, Segmented, EmptyState, StatTile } from '../design/ui';
import { auth } from '../firebase';
import {
    panggilBoost, mesejRalat, tarikhPendek, muatTurunCsv, TAJUK_CSV, barisCsv,
    LABEL_STATUS, TONE_STATUS, LABEL_SHEET, TONE_SHEET, JENIS,
} from '../services/boostService';

const LABEL_PROG_STATUS = { buka: 'Open', segera: 'Coming soon', tutup: 'Hidden' };

export default function BoostPartnerView() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [ralatMuat, setRalatMuat] = useState('');
    const [progId, setProgId] = useState('');
    const [cari, setCari] = useState('');

    const mParam = params.get('m') || '';
    /* `.then`, bukan async/await: lint React (set-state-in-effect) hanya menerima setState dalam callback. */
    const muat = useCallback((mcnId) => panggilBoost('partnerMuat', { mcnId: mcnId || mParam }).then(
        (h) => {
            setRalatMuat('');
            setData(h);
            setProgId((lama) => (h.program.some((p) => p.id === lama) ? lama : (h.program[0]?.id || '')));
        },
        (e) => setRalatMuat(mesejRalat(e)),
    ), [mParam]);
    useEffect(() => { muat(); }, [muat]);

    const program = data?.program.find((p) => p.id === progId) || null;
    const mohonProg = useMemo(() => (data?.permohonan || []).filter((m) => m.programId === progId), [data, progId]);
    const ditapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        if (!q) return mohonProg;
        return mohonProg.filter((m) => [m.profil?.nama, m.email, m.profil?.wa, m.profil?.tiktok1, m.profil?.tiktok2, m.profil?.shopee]
            .some((v) => String(v || '').toLowerCase().includes(q)));
    }, [mohonProg, cari]);

    const kepala = (
        <PageHeader
            title="MCN partner"
            subtitle={data?.mcn ? `Applications for ${data.mcn.nama}` : 'Applications from SESAAT customers'}
            sisiTajuk={<Users size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)' }} />}
            suisBahasa={false}
            actions={<Btn variant="ghost" size="sm" icon={<RefreshCw size={14} strokeWidth={1.9} />} onClick={() => { setData(null); muat(data?.mcn?.id); }}>Refresh</Btn>}
        />
    );

    let isi;
    if (ralatMuat) {
        isi = <EmptyState title="Could not load" action={<Btn variant="ghost" onClick={() => { setRalatMuat(''); muat(); }}>Try again</Btn>}>{ralatMuat}</EmptyState>;
    } else if (!data) {
        isi = <div className="s-dim" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '24px 0' }}><Loader2 size={16} className="animate-spin" /> Loading…</div>;
    } else if (!data.mcn) {
        isi = (
            <EmptyState title="This account isn't linked to an MCN">
                Ask the SESAAT admin to add <strong>{auth.currentUser?.email || 'your email'}</strong> as a partner of your MCN, then refresh this page.
            </EmptyState>
        );
    } else {
        const lulus = mohonProg.filter((m) => m.status === 'lulus').length;
        const ditolak = mohonProg.filter((m) => m.status === 'ditolak').length;
        const belumSheet = mohonProg.filter((m) => m.status !== 'ditolak' && m.sheet?.status !== 'ok').length;
        isi = (
            <>
                {data.mcnSenarai.length > 1 && (
                    <div style={{ marginBottom: 14 }}>
                        <Segmented value={data.mcn.id}
                            onChange={(id) => { setData(null); setParams({ m: id }); /* muat semula melalui useEffect (params berubah) */ }}
                            options={data.mcnSenarai.map((m) => ({ value: m.id, label: m.nama }))} />
                    </div>
                )}

                {data.program.length === 0 ? (
                    <EmptyState title="No programs yet">The SESAAT admin hasn't set up a program for {data.mcn.nama} yet.</EmptyState>
                ) : (
                    <>
                        <div style={{ marginBottom: 16 }}>
                            <Segmented value={progId} onChange={(v) => { setProgId(v); setCari(''); }}
                                options={data.program.map((p) => ({
                                    value: p.id,
                                    label: `${p.label} · ${(data.permohonan || []).filter((m) => m.programId === p.id).length}`,
                                }))} />
                        </div>

                        {program && (
                            <>
                                <p className="s-dim" style={{ margin: '0 0 14px' }}>
                                    Program status: <strong>{LABEL_PROG_STATUS[program.status] || program.status}</strong>
                                    {' · '}{program.autoApprove ? 'Auto approve on — customers are approved as soon as they apply.' : 'Manual approve — approve each application below.'}
                                </p>

                                <KadSheet key={program.id} program={program} emelRobot={data.emelRobot}
                                    belumSheet={belumSheet} onBerubah={() => muat(data.mcn.id)} />

                                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', margin: '18px 0' }}>
                                    <StatTile label="Applications" value={mohonProg.length} />
                                    <StatTile label="Approved" value={lulus} />
                                    <StatTile label="Rejected" value={ditolak} />
                                    <StatTile label="Not in sheet" value={belumSheet} />
                                </div>

                                <SenaraiPemohon senarai={ditapis} jumlah={mohonProg.length} cari={cari} setCari={setCari}
                                    autoApprove={program.autoApprove}
                                    namaFail={`${data.mcn.id}-${program.jenis}-applications.csv`}
                                    onBerubah={() => muat(data.mcn.id)} />
                                {data.terpotong && <p className="s-hint">Showing the newest applications only. Export may be incomplete — contact SESAAT admin.</p>}
                            </>
                        )}
                    </>
                )}
            </>
        );
    }

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>
            {kepala}
            {isi}
        </div>
    );
}

/* ── Kad Google Sheet satu program ───────────────────────────── */
function KadSheet({ program, emelRobot, belumSheet, onBerubah }) {
    const s = program.sheet || {};
    const [url, setUrl] = useState(s.url || '');
    const [sibuk, setSibuk] = useState('');
    const [mesej, setMesej] = useState({ jenis: '', teks: '' });
    const [ujian, setUjian] = useState(s.ujian || null);
    const [disalin, setDisalin] = useState(false);
    const berubah = url.trim() !== (s.url || '');

    async function simpan(on) {
        setSibuk('simpan'); setMesej({ jenis: '', teks: '' });
        try {
            await panggilBoost('partnerSheetSimpan', { programId: program.id, on, url: url.trim() });
            setMesej({ jenis: 'ok', teks: on ? 'Saved. New applications will be added to the sheet.' : 'Saved. Auto save is off.' });
            onBerubah();
        } catch (e) {
            setMesej({ jenis: 'ralat', teks: mesejRalat(e) });
        }
        setSibuk('');
    }
    async function uji() {
        setSibuk('uji'); setMesej({ jenis: '', teks: '' });
        try {
            const h = await panggilBoost('partnerSheetUji', { programId: program.id, url: berubah ? url.trim() : '' });
            setUjian(h.ujian);
        } catch (e) {
            setMesej({ jenis: 'ralat', teks: mesejRalat(e) });
        }
        setSibuk('');
    }
    async function hantar() {
        setSibuk('hantar'); setMesej({ jenis: '', teks: '' });
        try {
            const h = await panggilBoost('partnerHantarSheet', { programId: program.id }, { timeout: 180000 });
            const teks = h.gagal
                ? `${h.ok} added, ${h.gagal} failed: ${h.mesejGagal}`
                : h.baki > 0 ? `${h.ok} added. ${h.baki} left — press Send again.` : `${h.ok} added to the sheet.`;
            setMesej({ jenis: h.gagal ? 'ralat' : 'ok', teks });
            onBerubah();
        } catch (e) {
            setMesej({ jenis: 'ralat', teks: mesejRalat(e) });
        }
        setSibuk('');
    }
    function salin() {
        try { navigator.clipboard.writeText(emelRobot); setDisalin(true); setTimeout(() => setDisalin(false), 1500); } catch { /* browser lama */ }
    }

    return (
        <Card>
            <CardBody>
                <div className="s-togrow" style={{ marginBottom: 14 }}>
                    <div className="s-togrow__txt">
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>Auto save to Google Sheet</p>
                        <p className="s-dim" style={{ margin: '2px 0 0' }}>Each new application is added as a new row in your sheet.</p>
                    </div>
                    <button type="button" role="switch" aria-checked={!!s.on} aria-label="Auto save to Google Sheet"
                        className={s.on ? 's-switch s-switch--on' : 's-switch'} disabled={!!sibuk}
                        onClick={() => simpan(!s.on)}>
                        <span className="s-switch__dot" />
                    </button>
                </div>

                <label className="s-lbl" htmlFor="bp-url">Sheet link</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input id="bp-url" className="s-input" style={{ flex: '1 1 320px', minWidth: 0 }}
                        placeholder="https://docs.google.com/spreadsheets/d/…" value={url}
                        onChange={(e) => setUrl(e.target.value)} disabled={!!sibuk} />
                    <Btn variant="ghost" onClick={() => simpan(!!s.on)} disabled={!!sibuk || !berubah}>Save link</Btn>
                </div>
                <p className="s-hint">Open the right tab in your sheet, then copy the link from the address bar. Row 1 must be the column titles.</p>

                <label className="s-lbl" style={{ marginTop: 14 }}>Share your sheet with this email as Editor</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{
                        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontSize: 12.5, background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                        borderRadius: 'var(--s-r-sm)', padding: '8px 10px', color: 'var(--s-ink)',
                    }}>{emelRobot}</code>
                    <Btn variant="ghost" size="sm" icon={disalin ? <Check size={13} strokeWidth={2.2} /> : <Copy size={13} strokeWidth={1.9} />} onClick={salin}>
                        {disalin ? 'Copied' : 'Copy'}
                    </Btn>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--s-line)' }}>
                    <Btn variant="ghost" onClick={uji} disabled={!!sibuk || !url.trim()}
                        icon={sibuk === 'uji' ? <Loader2 size={14} className="animate-spin" /> : null}>Test connection</Btn>
                    {s.on && belumSheet > 0 && (
                        <Btn onClick={hantar} disabled={!!sibuk || berubah}
                            icon={sibuk === 'hantar' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.9} />}>
                            Send to sheet ({belumSheet} not sent)
                        </Btn>
                    )}
                    {ujian && <HasilUjian ujian={ujian} />}
                </div>
                {berubah && <p className="s-hint">Link changed — press Save link before sending.</p>}
                {mesej.teks && (
                    <p role="status" style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.5, color: mesej.jenis === 'ralat' ? 'var(--s-stop)' : 'var(--s-ok)' }}>{mesej.teks}</p>
                )}
            </CardBody>
        </Card>
    );
}

function HasilUjian({ ujian }) {
    if (!ujian.ok) {
        return <span style={{ fontSize: 13, color: 'var(--s-stop)', lineHeight: 1.5 }}>Not connected — {ujian.mesej}</span>;
    }
    return (
        <span style={{ fontSize: 13, color: 'var(--s-ok)', lineHeight: 1.5 }}>
            Connected{ujian.tab ? ` to tab "${ujian.tab}"` : ''} · {ujian.dikenal.length} column{ujian.dikenal.length === 1 ? '' : 's'} matched ({ujian.dikenal.join(', ')})
            {ujian.takDikenal?.length > 0 && <span className="s-dim"> · left blank: {ujian.takDikenal.join(', ')}</span>}
        </span>
    );
}

/* ── Senarai pemohon ────────────────────────────────────────── */
export function SenaraiPemohon({ senarai, jumlah, cari, setCari, autoApprove, namaFail, onBerubah, tunjukProgram = false }) {
    const [tolakId, setTolakId] = useState('');
    const [sebab, setSebab] = useState('');
    const [sibuk, setSibuk] = useState('');
    const [ralat, setRalat] = useState('');

    async function ubah(aksi, id, data = {}) {
        setSibuk(id); setRalat('');
        try {
            await panggilBoost(aksi, { mohonId: id, ...data });
            setTolakId(''); setSebab('');
            onBerubah();
        } catch (e) {
            setRalat(mesejRalat(e));
        }
        setSibuk('');
    }

    return (
        <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 0 }}>
                    <Search size={15} strokeWidth={1.9} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--s-ink-3)' }} />
                    <input className="s-input" style={{ paddingLeft: 32 }} placeholder="Search name, email, WhatsApp or username"
                        value={cari} onChange={(e) => setCari(e.target.value)} />
                </div>
                <Btn variant="ghost" size="sm" icon={<Download size={14} strokeWidth={1.9} />} disabled={!senarai.length}
                    onClick={() => muatTurunCsv(namaFail, TAJUK_CSV, senarai.map(barisCsv))}>
                    Export CSV ({senarai.length})
                </Btn>
            </div>
            {ralat && <p role="alert" style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--s-stop)' }}>{ralat}</p>}

            {senarai.length === 0 ? (
                <EmptyState title={jumlah ? 'No matching applications' : 'No applications yet'}>
                    {jumlah ? 'Try a different search.' : 'Applications appear here as soon as customers apply.'}
                </EmptyState>
            ) : (
                <div className="s-tblwrap">
                    <table className="s-table s-table--rapat">
                        <thead>
                            <tr>
                                <th>Date</th>
                                {tunjukProgram && <th>Program</th>}
                                <th>Name</th>
                                <th>Email</th>
                                <th>WhatsApp</th>
                                <th>TikTok</th>
                                <th>Shopee</th>
                                <th>Status</th>
                                <th>Sheet</th>
                                <th style={{ textAlign: 'right' }} />
                            </tr>
                        </thead>
                        <tbody>
                            {senarai.map((m) => (
                                <BarisPemohon key={m.id} m={m} tunjukProgram={tunjukProgram} autoApprove={autoApprove}
                                    sibuk={sibuk === m.id} tolakBuka={tolakId === m.id} sebab={sebab} setSebab={setSebab}
                                    onTolakBuka={() => { setTolakId(m.id); setSebab(''); }}
                                    onTolakBatal={() => setTolakId('')}
                                    onTolak={() => ubah('partnerTolak', m.id, { sebab })}
                                    onLulus={() => ubah('partnerLulus', m.id)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

function BarisPemohon({ m, tunjukProgram, autoApprove, sibuk, tolakBuka, sebab, setSebab, onTolakBuka, onTolakBatal, onTolak, onLulus }) {
    const p = m.profil || {};
    const lajur = 9 + (tunjukProgram ? 1 : 0);
    return (
        <>
            <tr>
                <td style={{ whiteSpace: 'nowrap' }}>{tarikhPendek(m.dicipta)}</td>
                {tunjukProgram && <td style={{ whiteSpace: 'nowrap' }}>{m.mcnNama ? `${m.mcnNama} · ` : ''}{JENIS[m.jenis]?.label || m.jenis}</td>}
                <td className="s-td-strong">{p.nama}</td>
                <td>{m.email}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{p.wa}</td>
                <td>{[p.tiktok1, p.tiktok2].filter(Boolean).map((u) => `@${u}`).join(', ') || <span className="s-dim">—</span>}</td>
                <td>{p.shopee || <span className="s-dim">—</span>}</td>
                <td><Badge tone={TONE_STATUS[m.status] || 'neutral'}>{LABEL_STATUS[m.status] || m.status}</Badge></td>
                <td title={m.sheet?.mesej || ''}>
                    <Badge tone={TONE_SHEET[m.sheet?.status] || 'neutral'}>{LABEL_SHEET[m.sheet?.status] || '—'}</Badge>
                    {m.sheet?.status === 'gagal' && m.sheet?.mesej && <div className="s-dim" style={{ fontSize: 11.5, marginTop: 4, maxWidth: 220 }}>{m.sheet.mesej}</div>}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {sibuk ? <Loader2 size={14} className="animate-spin" /> : m.status === 'ditolak' ? (
                        <Btn variant="quiet" size="sm" onClick={onLulus}>Approve again</Btn>
                    ) : (
                        <>
                            {m.status === 'menunggu' && !autoApprove && <Btn size="sm" onClick={onLulus}>Approve</Btn>}{' '}
                            <Btn variant="quiet" size="sm" onClick={onTolakBuka}>Reject</Btn>
                        </>
                    )}
                </td>
            </tr>
            {m.status === 'ditolak' && m.sebabTolak && (
                <tr><td colSpan={lajur} className="s-dim" style={{ paddingTop: 0 }}>Reject note: {m.sebabTolak}</td></tr>
            )}
            {tolakBuka && (
                <tr>
                    <td colSpan={lajur}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span className="s-dim">Reject {p.nama}? They lose access to this program and the WhatsApp group link.</span>
                            <input className="s-input" style={{ flex: '1 1 220px', minWidth: 0 }} placeholder="Internal note (optional)"
                                value={sebab} onChange={(e) => setSebab(e.target.value)} maxLength={300} />
                            <Btn size="sm" onClick={onTolak}>Reject</Btn>
                            <Btn variant="ghost" size="sm" onClick={onTolakBatal}>Cancel</Btn>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
